import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from './client'
import type { Alert, AlertPreference, AlertSummary, KillSwitch, PanicReport } from './types'

/** El estado del freno de emergencia. Se refresca solo y seguido: lo puede
 *  haber bajado otra pestaña, otra persona o el que está mirando el teléfono
 *  desde el colectivo, y una app que muestra "operando" cuando todo está
 *  frenado es peor que no mostrar nada. */
export function useKillSwitch() {
  return useQuery({
    queryKey: ['kill-switch'],
    queryFn: () => apiFetch<KillSwitch>('/control/kill-switch'),
    refetchInterval: 15_000,
  })
}

function useKillSwitchAction(action: 'activate' | 'deactivate') {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (reason: string) =>
      apiFetch<KillSwitch>(`/control/kill-switch/${action}`, {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: (state) => {
      queryClient.setQueryData(['kill-switch'], state)
      void queryClient.invalidateQueries({ queryKey: ['alerts'] })
    },
  })
}

export const useActivateKillSwitch = () => useKillSwitchAction('activate')
export const useDeactivateKillSwitch = () => useKillSwitchAction('deactivate')

export function useAlerts(pending = false, kind?: string) {
  return useQuery({
    queryKey: ['alerts', { pending, kind }],
    queryFn: () =>
      apiFetch<Alert[]>(`/alerts?pending=${pending}${kind ? `&kind=${kind}` : ''}`),
    refetchInterval: 30_000,
  })
}

export function useAlertSummary() {
  return useQuery({
    queryKey: ['alerts', 'summary'],
    queryFn: () => apiFetch<AlertSummary>('/alerts/summary'),
    refetchInterval: 30_000,
  })
}

export function useAcknowledgeAlert() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ message: string }>(`/alerts/${id}/acknowledge`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alerts'] }),
  })
}

export function useAcknowledgeAllAlerts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<{ message: string }>('/alerts/acknowledge-all', { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alerts'] }),
  })
}

export function useAlertFollowup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ alertId, question }: { alertId: number; question: string }) =>
      apiFetch<Alert>(`/alerts/${alertId}/followup`, { method: 'POST', body: { question } }),
    onSuccess: (updated) => {
      queryClient.setQueriesData<Alert[]>({ queryKey: ['alerts'] }, (cached) =>
        Array.isArray(cached)
          ? cached.map((alert) => (alert.id === updated.id ? updated : alert))
          : cached,
      )
    },
  })
}

/** Overrides de canal/umbral por tipo de evento — siempre trae las 9
 *  filas del catálogo (personalizadas o no), ver app/services/
 *  alert_preferences.py::ALERT_KIND_LABELS. */
export function useAlertPreferences() {
  return useQuery({
    queryKey: ['alert-preferences'],
    queryFn: () => apiFetch<AlertPreference[]>('/alert-preferences'),
  })
}

export function useUpdateAlertPreference() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      kind,
      channels,
      min_severity,
    }: {
      kind: string
      channels: string[]
      min_severity: string
    }) =>
      apiFetch<AlertPreference>(`/alert-preferences/${kind}`, {
        method: 'PATCH',
        body: { channels, min_severity },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alert-preferences'] }),
  })
}

export function useResetAlertPreference() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (kind: string) =>
      apiFetch<{ message: string }>(`/alert-preferences/${kind}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alert-preferences'] }),
  })
}

/** El botón de pánico sobre una cuenta: cancela todo y aplana todo.
 *
 *  Invalida medio mundo al volver porque cambia medio mundo: los bots quedan
 *  pausados, las posiciones cerradas y las órdenes canceladas. */
export function usePanic() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (accountId: number) =>
      apiFetch<PanicReport>(`/control/panic/${accountId}`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['bots'] })
      void queryClient.invalidateQueries({ queryKey: ['alerts'] })
      void queryClient.invalidateQueries({ queryKey: ['exchanges', 'accounts'] })
    },
  })
}
